import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, User as UserIcon } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectCurrentClinicId } from '../../../store/slices/authSlice';
import type { User } from '../../../services/usersService';
import type { Api } from '../../../types/generated';

interface FormData {
  name: string;
  email: string;
  phone: string;
  roles: ('Owner' | 'Manager' | 'Prescriber' | 'Staff')[];
}

interface ValidationErrors {
  name?: string;
  email?: string;
  phone?: string;
  roles?: string;
}

const validateForm = (data: FormData): ValidationErrors => {
  const errors: ValidationErrors = {};

  // Required field validation
  if (!data.name?.trim()) errors.name = 'Name is required';
  if (!data.email?.trim()) errors.email = 'Email is required';
  if (!data.phone?.trim()) errors.phone = 'Phone number is required';
  if (!data.roles || data.roles.length === 0) errors.roles = 'At least one role is required';

  // Format validation
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Please enter a valid email address';
  }
  
  if (data.phone && !/^[\d\s\-()+.]{10,}$/.test(data.phone.replace(/\s/g, ''))) {
    errors.phone = 'Please enter a valid phone number';
  }

  return errors;
};

interface EditUserModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (userId: string, data: Partial<Api.User>) => Promise<void>;
  loading?: boolean;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  isOpen,
  onClose,
  onSave,
  loading = false
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    roles: ['Staff']
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const currentClinicId = useSelector(selectCurrentClinicId);

  // Initialize form data when user changes
  useEffect(() => {
    if (user && isOpen) {
      const initialFormData = {
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        roles: (user.roles || ['Staff']) as ('Owner' | 'Manager' | 'Prescriber' | 'Staff')[]
      };
      setFormData(initialFormData);
      setHasFormChanges(false);
      setErrors({});
    }
  }, [user, isOpen]);

  // Check if form has changes
  useEffect(() => {
    if (user) {
      const hasChanges = 
        formData.name !== (user.name || '') ||
        formData.email !== (user.email || '') ||
        formData.phone !== (user.phone || '') ||
        JSON.stringify(formData.roles.sort()) !== JSON.stringify((user.roles || ['Staff']).sort());
      
      setHasFormChanges(hasChanges);
    }
  }, [formData, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    console.log('[EditUserModal] Form submitted', { user, formData, hasFormChanges });

    setIsSubmitting(true);

    // Validate form data
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    console.log('[EditUserModal] Validation errors:', validationErrors);

    // If there are validation errors, don't submit
    if (Object.keys(validationErrors).length > 0) {
      console.log('[EditUserModal] Validation failed, not submitting');
      setIsSubmitting(false);
      return;
    }

    // Don't submit if no changes
    if (!hasFormChanges) {
      console.log('[EditUserModal] No changes detected, closing modal');
      onClose();
      setIsSubmitting(false);
      return;
    }

    try {
      // Only send changed fields
      const updateData: Partial<Api.User> = {};
      
      if (formData.name !== (user.name || '')) {
        updateData.name = formData.name.trim();
      }
      
      if (formData.email !== (user.email || '')) {
        updateData.email = formData.email.trim();
      }
      
      if (formData.phone !== (user.phone || '')) {
        updateData.phone = formData.phone.trim();
      }
      
      if (JSON.stringify(formData.roles.sort()) !== JSON.stringify((user.roles || ['Staff']).sort())) {
        (updateData as any).roles = formData.roles;
      }

      console.log('[EditUserModal] Sending PATCH with data:', updateData);

      await onSave(user.id, updateData);
      onClose();
    } catch (error) {
      console.error('Failed to update user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRoleChange = (role: 'Owner' | 'Manager' | 'Prescriber' | 'Staff', checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      roles: checked 
        ? [...prev.roles, role]
        : prev.roles.filter(r => r !== role)
    }));
    // Clear role error when user makes a selection
    if (errors.roles) {
      setErrors(prev => ({ ...prev, roles: undefined }));
    }
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-l-4 border-l-emerald-500 shadow-lg shadow-emerald-100/50">
        <DialogHeader>
          <DialogTitle className="text-xl font-syne font-medium text-vitl-navy flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-500 rounded-full flex items-center justify-center text-white font-syne font-medium text-lg">
              <UserIcon className="w-6 h-6" />
            </div>
            <div>
              <div>Edit User: {user.name}</div>
              <p className="text-sm text-vitl-dark/70 font-lato font-normal">
                Update user information and permissions
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2">
              User Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
                  className={`mt-1 ${errors.name ? 'border-red-500' : 'focus:border-emerald-400'}`}
                  placeholder="John Doe"
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                  className={`mt-1 ${errors.email ? 'border-red-500' : 'focus:border-emerald-400'}`}
                  placeholder="john@example.com"
                  required
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('phone', e.target.value)}
                  className={`mt-1 ${errors.phone ? 'border-red-500' : 'focus:border-emerald-400'}`}
                  placeholder="(555) 123-4567"
                  required
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Roles * <span className="text-xs font-normal text-gray-500">(Select all that apply)</span>
                </Label>
                <div className={`mt-2 space-y-3 p-3 border rounded-md ${errors.roles ? 'border-red-500' : 'border-gray-300'}`}>
                  {[
                    { value: 'Staff', description: 'Can view patients and basic information' },
                    { value: 'Prescriber', description: 'Can create and manage orders' },
                    { value: 'Manager', description: 'Can manage users and clinic settings' },
                    { value: 'Owner', description: 'Has full access to all features' }
                  ].map((role) => (
                    <div key={role.value} className="flex items-start space-x-3">
                      <div className="flex items-center h-5">
                        <input
                          id={`role-${role.value}`}
                          type="checkbox"
                          checked={formData.roles.includes(role.value as any)}
                          onChange={(e) => handleRoleChange(role.value as any, e.target.checked)}
                          className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor={`role-${role.value}`} className="text-sm font-medium text-gray-700 cursor-pointer">
                          {role.value}
                        </label>
                        <p className="text-xs text-gray-500">{role.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.roles && (
                  <p className="mt-1 text-sm text-red-600">{errors.roles}</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-emerald-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading || isSubmitting}
              className="border-gray-300 hover:border-emerald-400"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white"
              disabled={loading || isSubmitting || !hasFormChanges}
            >
              {loading || isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : hasFormChanges ? (
                'Save Changes'
              ) : (
                'No Changes'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;